import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useVacationColors } from 'dashboard409a/hooks/useVacationColors';

const toPickerHex = (color: string): string => {
  const matches = /rgba?\((\d+),\s*(\d+),\s*(\d+)/.exec(color);
  if (matches) {
    const r = Number.parseInt(matches[1], 10).toString(16).padStart(2, '0');
    const g = Number.parseInt(matches[2], 10).toString(16).padStart(2, '0');
    const b = Number.parseInt(matches[3], 10).toString(16).padStart(2, '0');
    return `#${r}${g}${b}`;
  }
  if (color.length === 9 && color.startsWith('#')) return color.slice(0, 7);
  return color;
};

interface GoogleCalendarSettingsModalProps {
  open: boolean;
  showVacationEvents: boolean;
  onClose: () => void;
  onShowVacationEventsChange: (show: boolean) => void;
}

export const GoogleCalendarSettingsModal = ({
  open,
  showVacationEvents,
  onClose,
  onShowVacationEventsChange,
}: GoogleCalendarSettingsModalProps) => {
  const { colors, handleChange, handleReset } = useVacationColors();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Google Calendar settings</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Control how Google Calendar data appears on the models calendar.
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={showVacationEvents}
              onChange={e => onShowVacationEventsChange(e.target.checked)}
            />
          }
          label="Show vacation events"
        />

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            Vacation event colors
          </Typography>
          <Tooltip title="Reset to defaults" arrow>
            <Button size="small" onClick={handleReset} variant="text">
              Reset
            </Button>
          </Tooltip>
        </Box>

        <Stack spacing={1.5}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" sx={{ minWidth: 80 }}>Background</Typography>
            <TextField
              type="color"
              value={toPickerHex(colors.bg)}
              onChange={e => handleChange('bg', e.target.value)}
              size="small"
              sx={{ width: 56 }}
              inputProps={{ style: { padding: 2, height: 32, cursor: 'pointer' } }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" sx={{ minWidth: 80 }}>Text</Typography>
            <TextField
              type="color"
              value={toPickerHex(colors.text)}
              onChange={e => handleChange('text', e.target.value)}
              size="small"
              sx={{ width: 56 }}
              inputProps={{ style: { padding: 2, height: 32, cursor: 'pointer' } }}
            />
          </Box>
        </Stack>

        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Preview
          </Typography>
          <Box
            sx={{
              width: '100%',
              height: 22,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 0.5,
              padding: '0 6px',
              backgroundColor: colors.bg,
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            <CalendarMonthIcon sx={{ color: colors.text, opacity: 0.75, fontSize: '0.85rem', flexShrink: 0 }} />
            <Typography
              variant="caption"
              fontWeight="bold"
              sx={{
                color: colors.text,
                lineHeight: 1.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
                minWidth: 0,
              }}
            >
              Out of office
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Done</Button>
      </DialogActions>
    </Dialog>
  );
};
